import { ICreateAffiliationPayload } from '@homeserve/od-typings/api';
import cloneDeep from 'lodash/cloneDeep';
import { ActionContext } from 'vuex';
import { getStoreAccessors } from 'vuex-typescript';

import { IAugmentedEvent } from '../components/WLFollowOrder/types';
import { IAddress } from './address';

type OrderContext = ActionContext<OrderState, any>;

export interface Person {
  address: IAddress | null;
  country: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  title: string;
}

export interface Order {
  acceptConditions: boolean;
  acceptCommercialContactEmailAndSMS: boolean;
  acceptCommercialContactMailAndPhone: boolean;
  acceptRetractationDelay: boolean;
  billingPerson: Person;
  deliveryPerson: Person;
  differentBilling: boolean;
  housingOlderThan2Years: boolean;
}

export interface IOrderAffiliation extends ICreateAffiliationPayload {
  email: string;
}

export interface OrderState {
  affiliation?: IOrderAffiliation;
  currentStep: number;
  followOrderEvents: IAugmentedEvent[];
  order: Order | null;
  shouldCustomerValidateOrder: boolean;
  trackingUUID: string | null;
}

const initialOrder: Order = {
  acceptConditions: false,
  acceptCommercialContactEmailAndSMS: true,
  acceptCommercialContactMailAndPhone: true,
  acceptRetractationDelay: false,
  billingPerson: {
    email: '',
    title: '',
    address: {
      streetNo: '',
      street: '',
      postCode: '',
      town: '',
      postalBox: '',
      companyName: '',
      country: '',
      residence: '',
      additionalAddress: '',
      flatNo: '',
      formattedAddress: '',
    },
    firstname: '',
    lastname: '',
    phone: '',
    country: '',
  },
  deliveryPerson: {
    email: '',
    title: '',
    address: {
      streetNo: '',
      street: '',
      postCode: '',
      town: '',
      postalBox: '',
      companyName: '',
      country: '',
      residence: '',
      additionalAddress: '',
      flatNo: '',
      formattedAddress: '',
    },
    firstname: '',
    lastname: '',
    phone: '',
    country: '',
  },
  differentBilling: false,
  housingOlderThan2Years: true,
};

const state: OrderState = {
  currentStep: 0,
  followOrderEvents: [],
  order: { ...initialOrder },
  shouldCustomerValidateOrder: true,
  trackingUUID: null,
};

const getters = {
  affiliation(s: OrderState) {
    return s.affiliation;
  },
  shouldCustomerValidateOrder(s: OrderState) {
    return s.shouldCustomerValidateOrder;
  },
  order(s: OrderState) {
    return s.order;
  },
  currentStep(s: OrderState) {
    return s.currentStep;
  },
  trackingUUID(s: OrderState) {
    return s.trackingUUID;
  },
  followOrderEvents(s: OrderState) {
    return s.followOrderEvents;
  },
};

const mutations = {
  affiliation(s: OrderState, affiliation: OrderState['affiliation']) {
    s.affiliation = affiliation;
  },
  shouldCustomerValidateOrder(s: OrderState, payload: boolean) {
    s.shouldCustomerValidateOrder = payload;
  },
  order(s: OrderState, payload: Order) {
    s.order = { ...s.order, ...cloneDeep(payload) };
  },
  trackingUUID(s: OrderState, payload: string | null) {
    s.trackingUUID = payload;
  },
  incrementCurrentStep(s: OrderState) {
    s.currentStep++;
  },
  step(s: OrderState, payload: number) {
    s.currentStep = payload;
  },
  followOrderEvents(s: OrderState, payload: IAugmentedEvent[]) {
    s.followOrderEvents = payload;
  },
};

const actions = {
  setAffiliation(context: OrderContext, affiliation: IOrderAffiliation) {
    context.commit('affiliation', affiliation);
  },
  setOrder(context: OrderContext, payload: Order) {
    mutateOrder(context, payload);
  },
  resetOrder(context: OrderContext) {
    mutateOrder(context, { ...initialOrder });
  },
  incrementCurrentStep(context: OrderContext) {
    context.commit('incrementCurrentStep');
  },
  setCurrentStep(context: OrderContext, step: number) {
    context.commit('step', step);
  },
  setFollowOrderEvents(context: OrderContext, payload: IAugmentedEvent[]) {
    context.commit('followOrderEvents', payload);
  },
};

export const order = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

const { dispatch, commit, read } = getStoreAccessors<OrderState, any>('app/order');

const getAffiliation = read(order.getters.affiliation);
const setAffiliation = dispatch(order.actions.setAffiliation);
const getTrackingUUID = read(order.getters.trackingUUID);
const getShouldCustomerValidateOrder = read(order.getters.shouldCustomerValidateOrder);
const getOrder = read(order.getters.order);
const setOrder = dispatch(order.actions.setOrder);
const setTrackingUUID = commit(order.mutations.trackingUUID);
const setShouldCustomerValidateOrder = commit(order.mutations.shouldCustomerValidateOrder);
const resetOrder = dispatch(order.actions.resetOrder);
const mutateOrder = commit(order.mutations.order);
const incrementCurrentStep = dispatch(order.actions.incrementCurrentStep);
const setCurrentStep = dispatch(actions.setCurrentStep);
const setFollowOrderEvents = dispatch(actions.setFollowOrderEvents);

export const orderHelper = {
  getAffiliation,
  setAffiliation,
  setOrder,
  setCurrentStep,
  setTrackingUUID,
  setShouldCustomerValidateOrder,
  incrementCurrentStep,
  resetOrder,
  getOrder,
  getShouldCustomerValidateOrder,
  getTrackingUUID,
  setFollowOrderEvents,
};

export { initialOrder };
